// src/AnimationView/engine/entities/EntityManager.ts
import Phaser from "phaser";

import type {
    CharacterConfig,
    ObjectConfig,
    TextObjectConfig,
} from "../../types/config";

import { Character } from "../entities/Character";
import { GameObject } from "../entities/Object";
import { TextObject } from "../entities/Text";
import { setArcadeBodySize } from "../utils/physics";

function writeCollisionData(
    go: Phaser.GameObjects.GameObject | undefined,
    data: {
        id: string;
        kind: "character" | "object" | "text";
        entity: any;
        flags?: { pickable?: boolean; immovable?: boolean; pushable?: boolean };
    }
) {
    if (!go) return;
    (go as any).setData?.("id", data.id);
    (go as any).setData?.("kind", data.kind);
    (go as any).setData?.("entity", data.entity);
    // compat: on garde les clés unitaires + l’objet "flags"
    const f = data.flags ?? {};
    (go as any).setData?.("flags", { pickable: !!f.pickable, immovable: !!f.immovable, pushable: !!f.pushable });
    (go as any).setData?.("pickable", !!f.pickable);
    (go as any).setData?.("immovable", !!f.immovable);
    (go as any).setData?.("pushable", !!f.pushable);
}


/** Optional adapters if your entity methods differ */
export interface EntityAdapters {
    character?: Partial<{
        create: (cfg: CharacterConfig) => Character;
        createPhaser: (e: Character, scene: Phaser.Scene) => void;
        update: (e: Character, cfg: CharacterConfig) => void;
        destroy: (e: Character) => void;
        getBody: (e: Character) =>
            | Phaser.Types.Physics.Arcade.ArcadeColliderType
            | undefined;
    }>;
    object?: Partial<{
        create: (cfg: ObjectConfig) => GameObject;
        createPhaser: (e: GameObject, scene: Phaser.Scene) => void;
        update: (e: GameObject, cfg: ObjectConfig) => void;
        destroy: (e: GameObject) => void;
        getBody: (e: GameObject) =>
            | Phaser.Types.Physics.Arcade.ArcadeColliderType
            | undefined;
    }>;
    text?: Partial<{
        create: (cfg: TextObjectConfig) => TextObject;
        createPhaser: (e: TextObject, scene: Phaser.Scene) => void;
        update: (e: TextObject, cfg: TextObjectConfig) => void;
        destroy: (e: TextObject) => void;
        getBody: (e: TextObject) =>
            | Phaser.Types.Physics.Arcade.ArcadeColliderType
            | undefined;
    }>;
}

export class EntityManager {
    private adminMode: boolean;
    private scene: Phaser.Scene;

    private characters = new Map<string, Character>();
    private objects = new Map<string, GameObject>();
    private texts = new Map<string, TextObject>();

    constructor(adminMode: boolean, scene: Phaser.Scene, private adapters: EntityAdapters = {}) {
        this.adminMode = adminMode;
        this.scene = scene;

        // hard cleanup on scene end
        scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.destroyAll());
        scene.events.once(Phaser.Scenes.Events.DESTROY, () => this.destroyAll());
    }

    // ───────────────────────────────── Entities: Characters ─────────────────────────────────

    syncCharacters(next: CharacterConfig[]) {
        this.#reconcile(
            this.characters,
            next,
            (c) => c.id,
            (cfg) => this.#makeCharacter(cfg),
            (ent, cfg) => this.#updateCharacter(ent, cfg),
            (ent) => this.#destroyCharacter(ent)
        );
    }

    getCharacter(id: string) {
        return this.characters.get(id);
    }

    getCharacterBodies(): Phaser.Types.Physics.Arcade.ArcadeColliderType[] {
        return Array.from(this.characters.values())
            .map((e) => this.#getBody(this.adapters.character, e)!)
            .filter(Boolean);
    }

    // ───────────────────────────────── Entities: Objects ───────────────────────────────────

    syncObjects(next: ObjectConfig[]) {
        this.#reconcile(
            this.objects,
            next,
            (c) => c.id,
            (cfg) => this.#makeObject(cfg),
            (ent, cfg) => this.#updateObject(ent, cfg),
            (ent) => this.#destroyObject(ent)
        );
    }

    getObject(id: string) {
        return this.objects.get(id);
    }

    getObjectBodies(): Phaser.Types.Physics.Arcade.ArcadeColliderType[] {
        return Array.from(this.objects.values())
            .map((e) => this.#getBody(this.adapters.object, e)!)
            .filter(Boolean);
    }

    // ───────────────────────────────── Entities: Texts ─────────────────────────────────────

    syncTexts(next: TextObjectConfig[]) {
        this.#reconcile(
            this.texts,
            next,
            (c) => c.id,
            (cfg) => this.#makeText(cfg),
            (ent, cfg) => this.#updateText(ent, cfg),
            (ent) => this.#destroyText(ent)
        );
    }

    getText(id: string) {
        return this.texts.get(id);
    }

    getTextBodies(): Phaser.Types.Physics.Arcade.ArcadeColliderType[] {
        return Array.from(this.texts.values())
            .map((e) => this.#getBody(this.adapters.text, e)!)
            .filter(Boolean);
    }


    // ───────────────────────────────── Bulk ops / Cleanup ──────────────────────────────────

    destroyAll() {
        // texts first (often no physics), then objects, then characters
        for (const e of this.texts.values()) this.#destroyText(e);
        for (const e of this.objects.values()) this.#destroyObject(e);
        for (const e of this.characters.values()) this.#destroyCharacter(e);
        this.texts.clear();
        this.objects.clear();
        this.characters.clear();
    }

    // ───────────────────────────────── Internals ───────────────────────────────────────────

    #reconcile<TEnt, TCfg>(
        map: Map<string, TEnt>,
        desired: TCfg[],
        idOf: (cfg: TCfg) => string,
        create: (cfg: TCfg) => TEnt,
        update: (ent: TEnt, cfg: TCfg) => void,
        destroy: (ent: TEnt) => void
    ) {
        const incoming = new Set(desired.map(idOf));

        // remove missing
        for (const [id, ent] of map) {
            if (!incoming.has(id)) {
                destroy(ent);
                map.delete(id);
            }
        }
        // add/update
        for (const cfg of desired) {
            const id = idOf(cfg);
            const existing = map.get(id);
            if (!existing) {
                const ent = create(cfg);
                map.set(id, ent);
            } else {
                update(existing, cfg);
            }
        }
    }

    // ───────────────────────── Character helpers ─────────────────────────

    #makeCharacter(cfg: CharacterConfig): Character {
        const e =
            this.adapters.character?.create?.(cfg) ?? new Character(this.adminMode, cfg);

        const createPhaser =
            this.adapters.character?.createPhaser ??
            ((x: any, s: Phaser.Scene) => x.createPhaserSprite?.(s));
        createPhaser(e, this.scene);

        const spr = this.#extractGO(e);
        if (spr) {
            spr.setDepth?.(10);
            spr.setVisible?.(true);
            if (typeof (e as any).x === "number" && typeof (e as any).y === "number") {
                spr.setPosition?.((e as any).x, (e as any).y);
            }
        }
        writeCollisionData(spr, {
            id: (e as any).id,
            kind: "character",
            entity: e,
            flags: {}, // rien de spécial côté perso
        });
        return e;
    }

    #updateCharacter(e: Character, cfg: CharacterConfig) {
        if (this.adapters.character?.update) {
            this.adapters.character.update(e, cfg);
            return;
        }
        (e as any).x = cfg.x;
        (e as any).y = cfg.y;
        (e as any).width = cfg.width;
        (e as any).height = cfg.height;

        const spr = this.#extractGO(e);
        if (spr) {
            spr.setPosition?.(cfg.x, cfg.y);
            spr.setDisplaySize?.(cfg.width, cfg.height);
            setArcadeBodySize(spr as any, cfg.width, cfg.height, "topLeft");
        }
        (e as any).updatePhaserSprite?.();
    }

    #destroyCharacter(e: Character) {
        (this.adapters.character?.destroy ?? ((x: any) => x.destroy?.()))(e);
    }

    // ───────────────────────── Object helpers ────────────────────────────

    #makeObject(cfg: ObjectConfig): GameObject {
        const e =
            this.adapters.object?.create?.(cfg) ?? new GameObject(this.adminMode, cfg);

        const createPhaser =
            this.adapters.object?.createPhaser ??
            ((x: any, s: Phaser.Scene) => x.createPhaserSprite?.(s));
        createPhaser(e, this.scene);

        const spr = this.#extractGO(e);
        if (spr) {
            spr.setPosition?.(cfg.x, cfg.y);
            spr.setDisplaySize?.(cfg.width, cfg.height);
            setArcadeBodySize(spr as any, cfg.width, cfg.height, "topLeft");

            const flags = { pickable: !!cfg.pickable, immovable: !!cfg.immovable, pushable: !!cfg.pushable };
            // Arcade: immovable si décor/immovable, sinon pushable => movable
            spr.setImmovable?.(!!cfg.immovable || !cfg.pushable);

            // flags consolidés + compat
            writeCollisionData(spr, {
                id: (e as any).id,
                kind: "object",
                entity: e,
                flags,
            });

            if (cfg.decor && spr.body) {
                (spr.body as Phaser.Physics.Arcade.Body).allowGravity = false;
            }
        }

        return e;
    }

    #updateObject(e: GameObject, cfg: ObjectConfig) {
        if (this.adapters.object?.update) {
            this.adapters.object.update(e, cfg);
            return;
        }
        (e as any).x = cfg.x;
        (e as any).y = cfg.y;
        (e as any).width = cfg.width;
        (e as any).height = cfg.height;
        (e as any).immovable = !!cfg.immovable;
        (e as any).pickable = !!cfg.pickable;
        (e as any).pushable = !!cfg.pushable;
        (e as any).decor = !!cfg.decor;

        const spr = this.#extractGO(e);
        if (spr) {
            spr.setPosition?.(cfg.x, cfg.y);
            spr.setDisplaySize?.(cfg.width, cfg.height);
            setArcadeBodySize(spr as any, cfg.width, cfg.height, "topLeft");
            // physics flags if Arcade body is present
            spr.setImmovable?.(!!cfg.immovable || !cfg.pushable);
            spr.setData?.("immovable", !!cfg.immovable);
            spr.setData?.("pushable", !!cfg.pushable);
            spr.setData?.("pickable", !!cfg.pickable);
            if (cfg.decor && spr.body) {
                (spr.body as Phaser.Physics.Arcade.Body).allowGravity = false;
            }
        }
        (e as any).updatePhaserSprite?.();
    }

    #destroyObject(e: GameObject) {
        (this.adapters.object?.destroy ?? ((x: any) => x.destroy?.()))(e);
    }

    // ───────────────────────── Text helpers ──────────────────────────────

    #makeText(cfg: TextObjectConfig): TextObject {
        const e =
            this.adapters.text?.create?.(cfg) ?? new TextObject(this.adminMode, cfg);

        const createPhaser =
            this.adapters.text?.createPhaser ??
            ((x: any, s: Phaser.Scene) => x.createPhaserText?.(s));
        createPhaser(e, this.scene);

        const t = this.#extractGO(e) as Phaser.GameObjects.Text | undefined;
        if (t) {
            t.setDepth?.(10);
            t.setVisible?.(true);
            if (typeof (e as any).x === "number" && typeof (e as any).y === "number") {
                t.setPosition((e as any).x, (e as any).y);
            }
        }
        return e;
    }

    #destroyText(e: TextObject) {
        (this.adapters.text?.destroy ?? ((x) => (x as any).destroy?.()))(e);
    }

    #updateText(e: TextObject, cfg: TextObjectConfig) {
        if (this.adapters.text?.update) {
            this.adapters.text.update(e, cfg);
            return;
        }
        (e as any).x = cfg.x;
        (e as any).y = cfg.y;

        const t = (e as any).textObj as Phaser.GameObjects.Text | undefined;
        if (t) {
            t.setText(cfg.textString ?? "");
            t.setPosition(cfg.x, cfg.y);
            if (cfg.color) t.setColor?.(cfg.color);
            if (cfg.backgroundColor) t.setBackgroundColor?.(cfg.backgroundColor);
            if (cfg.fontSize) t.setFontSize?.(cfg.fontSize);
            if (cfg.fontFamily) t.setFontFamily?.(cfg.fontFamily);
        }
    }

    // ───────────────────────── Common helpers ────────────────────────────

    /** Safely extract the underlying Phaser display object (sprite/image/text/container) */
    #extractGO(
        e: any
    ):
        | (Phaser.GameObjects.GameObject & {
            setDepth?(v: number): any;
            setVisible?(v: boolean): any;
            setPosition?(x: number, y: number): any;
            setDisplaySize?(w: number, h: number): any;
            setImmovable?(v: boolean): any;
            setData?(k: string, v: any): any;
            body?: Phaser.Physics.Arcade.Body;
        })
        | undefined {
        return e?.sprite ?? e?.image ?? e?.textObj ?? e?.container ?? undefined;
    }

    #getBody<
        T
    >(
        adapter:
            | EntityAdapters["character"]
            | EntityAdapters["object"]
            | EntityAdapters["text"],
        e: T
    ) {
        try {
            return (
                adapter?.getBody?.(e as any) ??
                (e as any).sprite ??
                (e as any).image ??
                (e as any).container ??
                (e as any).text ??
                (e as any).textObj
            );
        } catch {
            return undefined;
        }
    }
}
